import '@sass/instructionalVideos/instructionalVideos.scss';
import { classList, documentReady, fadeOut, getUrlVars, isMobileView, qs } from '@utils/toolbox';
import { ref, watch } from 'vue';

let playlist;

const instructionalVideos = {
	// Cache elements
	elems: {
		player: qs('#player-video'),
		title: qs('#player-title'),
		description: qs('#player-description'),
		playlist: qs('#playlist-items'),
		playlistControls: qs('#playlist-controls')
	},

	// Vue state
	currentIndex: ref(0),
	playlistControlIndex: ref(0),

	init () {
		classList('.abHeader__dropdownItem.abHeader__dropdownItem-instructional-videos').add('abHeader__dropdownItem--active');
		if (!isMobileView()) {
			classList(`.abHeader__link[href='/information']`).add('abHeader__link--active');
		}

		playlist = isMobileView() ? mobilePlaylist : desktopPlaylist;

		// Initial state from URL
		this.currentIndex.value = Number(getUrlVars().index) || 0;
		this.playlistControlIndex.value = this.currentIndex.value;

		// Initialise YouTube library
		youtubeApi.init();

		// Render UI
		this.renderPlaylist();
		this.renderPlaylistControls();

		// Bind events
		this.events();

		// Initial UI state
		this.renderCurrent(this.currentIndex.value);
		this.setActiveCarouselItem(this.playlistControlIndex.value);

		// Watch: when the video index changes, update player + details + list selection
		watch(this.currentIndex, (newIndex) => {
			this.renderCurrent(newIndex);

			// keep dots in sync with current playing item
			this.playlistControlIndex.value = newIndex;

			if (!isMobileView()) {
				this.playlistScrollToCurrent(newIndex);
			}
		});

		// Watch: when the carousel index changes, update dots + scroll
		watch(this.playlistControlIndex, (newIndex) => {
			this.setActiveCarouselItem(newIndex);
		});
	},

	events () {
		document.addEventListener('click', (event) => {
			const elem = event.target;

			// Playlist item click
			const listItem = elem.closest('.list-item');
			if (listItem) {
				const clickedIndex = Number(listItem.dataset.index);
				if (this.currentIndex.value === clickedIndex) {
					youtubeApi.togglePlay();
				} else {
					this.currentIndex.value = clickedIndex;
					// playlistControlIndex will sync via watch(currentIndex)
				}
				return;
			}

			// Carousel dots click
			const dot = elem.closest('.playlist-control');
			if (dot) {
				this.playlistControlIndex.value = Number(dot.dataset.index);
				return;
			}

			// Carousel left button
			if (elem.closest('.scroll-button.left')) {
				const min = 1;
				const max = playlist.length - 2;
				const next = this.playlistControlIndex.value - 1;
				this.playlistControlIndex.value = next >= min ? next : max;
				return;
			}

			// Carousel right button
			if (elem.closest('.scroll-button.right')) {
				const min = 1;
				const max = playlist.length - 2;
				const next = this.playlistControlIndex.value + 1;
				this.playlistControlIndex.value = next <= max ? next : min;
			}
		});
	},

	// Adds current class and scrolls carousel to current icon
	setActiveCarouselItem (index) {
		classList('.playlist-control.current').remove('current');
		classList(`.playlist-control[data-index='${index}']`).add('current');
		this.playlistScrollToCurrent(index);
	},

	playlistScrollToCurrent (index) {
		if (isMobileView()) {
			return;
		}
		const el = qs(`.list-item[data-index='${index}']`);
		if (el) {
			el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
		}
	},

	renderPlaylistControls () {
		let html = '';
		for (let i = 1; i < playlist.length - 1; i++) {
			html += `<span class="playlist-control" data-index="${i}"></span>`;
		}
		this.elems.playlistControls.innerHTML = html;
	},

	renderPlaylist () {
		this.elems.playlist.innerHTML = playlist.map((item, index) => this.renderPlaylistItem(item, index)).join('');
	},

	renderPlaylistItem (item, index) {
		return `<div class="list-item" data-index="${index}">
			<div class="list-image">
				<img src="/static/v${js_params.version}/images/instructional_videos/${index + (isMobileView() ? 'm' : '')}.png" alt="${item.title}">
				<div class="list-image-overlay"><i class="icon-play"></i></div>
				<div class="list-image-meta">${item.length}</div>
			</div>
			<div class="list-meta">
				<div class="list-meta-title">${item.title}</div>
				<div class="list-meta-description">${item.description}</div>
			</div>
		</div>`;
	},

	async renderCurrent (index) {
		const item = playlist[index];
		if (!item) {
			return;
		}

		const { title, description, id } = item;
		this.elems.title.innerText = title;
		this.elems.description.innerText = description;

		this.playlistControlIndex.value = index;

		youtubeApi.loadVideoById(id);

		await fadeOut('.list-item.current .list-image-overlay');

		classList('.list-item.current')
			.remove('current')
			.remove('paused')
			.remove('playing');


		classList(`.list-item[data-index='${index}']`).add('current');
	}

};

const youtubeApi = {
	player: {},
	isLoaded: false,

	init () {
		this.loadLibrary();
	},

	togglePlay () {
		const state = this.player.getPlayerState();
		if (state === 1) {
			this.player.pauseVideo();
		} else {
			this.player.playVideo();
		}
	},

	loadLibrary () {
		const tag = document.createElement('script');
		tag.src = 'https://www.youtube.com/iframe_api';
		const firstScriptTag = document.getElementsByTagName('script')[0];
		firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
	},

	loadVideoById (id) {
		if (this.isLoaded) {
			this.player.loadVideoById(id);
		}
	},

	loadLibraryCallback () {
		this.player = new YT.Player('player-video', {
			width: '100%',
			videoId: playlist[Number(getUrlVars().index) || 0].id,
			events: {
				onReady: this.onPlayerReady,
				onStateChange: this.onStateChange
			}
		});
	},

	onPlayerReady () {
		youtubeApi.isLoaded = true;
	},

	onStateChange (event) {
		switch (event.data) {
			case 0: // ended
				classList(`.list-item[data-index='${instructionalVideos.currentIndex.value}']`).add('watched');
				if (instructionalVideos.currentIndex.value + 1 > playlist.length - 1) {
					instructionalVideos.currentIndex.value = 0;
				} else {
					instructionalVideos.currentIndex.value += 1;
				}
				break;
			case 1: // playing
				classList(`.list-item[data-index='${instructionalVideos.currentIndex.value}'] i`).add('icon-pause').remove('icon-play');
				break;
			case 2: // paused
				classList(`.list-item[data-index='${instructionalVideos.currentIndex.value}'] i`).add('icon-play').remove('icon-pause');
				break;
		}
	}
};

// Must be global for YouTube API
function onYouTubeIframeAPIReady () {
	youtubeApi.loadLibraryCallback();
}
window.onYouTubeIframeAPIReady = onYouTubeIframeAPIReady;

const mobilePlaylist = [
	{
		id: '8cwrW24pIlQ',
		title: 'Klimatkompensation i app',
		description: 'På Flygresor.se kan du enkelt klimatkompensera din flygresa. Såhär gör du det!',
		length: '1:19'
	},
	{
		id: '0WbzJ2_W4ZY',
		title: 'Snabb genomgång av Flygresor.se i appen',
		description: 'Här kommer en snabb genomgång om hur Flygresor.se funkar!',
		length: '1:27'
	},
	{
		id: '6OLJ3qkRBgs',
		title: 'Datum i app',
		description: 'Se till att ange det datum du vill resa ut på och rätt datum för när du vill resa hem igen. På detta sätt får du rätt förutsättningar för ett bättre pris på din resa.',
		length: '0:36'
	},
	// {
	// 	id: 'ZtnmIBKbx1o',
	// 	title: 'Sortering i app',
	// 	description: 'Flygresor.se sorterar alltid sökresultatet med lägsta pris överst som standard. Självklart kan du som besökare ändra till ett annat sorteringsalternativ! Kolla in filmen som visar dig hur du gör.',
	// 	length: '0:37'
	// },
	{
		id: '1UEUNojqjgg',
		title: 'Priskalendern i app',
		description: 'Flygresor.se erbjuder flera hjälpmedel för att hitta de billigaste biljetterna. Bland annat priskalendern som fungerar bra för dig som är flexibel med utresa och hemresa',
		length: '1:09'
	},
	{
		id: 'nuF3JS0DdiA',
		title: 'Prispressaren i app',
		description: 'Prispressaren ger en bra överblick över vilken dag det är billigast att resa för dig - testa funktionen på Flygresor.se!',
		length: '0:35'
	},
	{
		id: 'vR4SukYRdns',
		title: 'Prisbevakaren i app',
		description: 'Väntar du på att flygpriserna till ett resmål ska sjunka? Låt Flygresor.se hjälpa dig. Lägg upp en prisbevakning som automatiskt meddelar dig via e-post när flygpriset till ett valt resmål sjunker under ett bestämt pris.',
		length: '1:18'
	},
	{
		id: 'DzdwlorkHD0',
		title: 'Ombokningsbart i app',
		description: 'Flygresor.se gör det enkelt för dig att hitta ombokningsbara flygbiljetter direkt när du jämför priser från olika resebyråer och flygbolag',
		length: '0:36'
	},
	{
		id: 'm5Ze4FTrEGo',
		title: 'Byten i app',
		description: 'Ibland vill man undvika för många byten när man flyger. På flygresor.se kan du filtrera bort alla resor med ett eller flera byten om du vill. Så här går det till.',
		length: '0:46'
	},
	{
		id: 'JxJryJC10DI',
		title: 'Bagage i app',
		description: 'På Flygresor.se kan du se vad priset på din flygbiljett blir inklusive bagage. Denna film visar hur du gör en sådan sökning på Flygresor.se.',
		length: '0:36'
	},
	{
		id: 'vDiUEAmsUtA',
		title: 'Tid i app',
		description: 'Här kommer ett tips på hur du kan filtrera din sökning kring tid. För att hitta de bästa alternativen för din resa, är det viktigt att du använder filtret på Flygresor.se.',
		length: '0:49'
	}
];

const desktopPlaylist = [
	{
		id: 'MV2s9-vtQQ4',
		title: 'Instruktionsfilm - Klimatkompensation',
		description: 'På Flygresor.se kan du enkelt klimatkompensera din flygresa. Såhär gör du det!',
		length: '1:27'
	},
	{
		id: 'duBJriEhLjQ',
		title: 'Instruktionsfilm - Snabb genomgång av Flygresor.se',
		description: 'Här kommer en snabb genomgång om hur Flygresor.se funkar!',
		length: '1:27'
	},
	{
		id: 'r2ZOMzlJlhY',
		title: 'Instruktionsfilm - Datum',
		description: 'Se till att ange det datum du vill resa ut på och rätt datum för när du vill resa hem igen. På detta sätt får du rätt förutsättningar för ett bättre pris på din resa.',
		length: '0:37'
	},
	// {
	// 	id: 'jKQDEyRqfwA',
	// 	title: 'Instruktionsfilm - Sortering',
	// 	description: 'Flygresor.se sorterar alltid sökresultatet med lägsta pris överst som standard. Självklart kan du som besökare ändra till ett annat sorteringsalternativ! Kolla in filmen som visar dig hur du gör.',
	// 	length: '0:37'
	// },
	{
		id: '0ki1e3O2okg',
		title: 'Instruktionsfilm - Priskalendern',
		description: 'Flygresor.se erbjuder flera hjälpmedel för att hitta de billigaste biljetterna. Bland annat priskalendern som fungerar bra för dig som är flexibel med utresa och hemresa',
		length: '1:00'
	},
	{
		id: 'MsQ8VOb-Axk',
		title: 'Instruktionsfilm - Prispressaren',
		description: 'Prispressaren ger en bra överblick över vilken dag det är billigast att resa för dig - testa funktionen på Flygresor.se!',
		length: '0:36'
	},
	{
		id: '0FF7YjCz6nU',
		title: 'Instruktionsfilm - Prisbevakaren',
		description: 'Väntar du på att flygpriserna till ett resmål ska sjunka? Låt Flygresor.se hjälpa dig. Lägg upp en prisbevakning som automatiskt meddelar dig via e-post när flygpriset till ett valt resmål sjunker under ett bestämt pris.',
		length: '1:22'
	},
	{
		id: 'gWSfgrdbDx0',
		title: 'Instruktionsfilm - Ombokningsbar',
		description: 'Flygresor.se gör det enkelt för dig att hitta ombokningsbara flygbiljetter direkt när du jämför priser från olika resebyråer och flygbolag',
		length: '0:40'
	},
	{
		id: 'UkKxbgQKd1M',
		title: 'Instruktionsfilm - Byten',
		description: 'Ibland vill man undvika för många byten när man flyger. På flygresor.se kan du filtrera bort alla resor med ett eller flera byten om du vill. Så här går det till.',
		length: '0:47'
	},
	{
		id: '6eBK0qRJ9gM',
		title: 'Instruktionsfilm - Bagage',
		description: 'På Flygresor.se kan du se vad priset på din flygbiljett blir inklusive bagage. Denna film visar hur du gör en sådan sökning på Flygresor.se.',
		length: '0:41'
	},
	{
		id: 'RwMkfe-lde4',
		title: 'Instruktionsfilm - Tid',
		description: 'Här kommer ett tips på hur du kan filtrera din sökning kring tid. För att hitta de bästa alternativen för din resa, är det viktigt att du använder filtret på Flygresor.se.',
		length: '0:50'
	}
];

documentReady(function () {
	instructionalVideos.init();
	window.instructionalVideos = instructionalVideos;
});
